import React, { useState } from "react";
import { Button, Card, Icon, Item, List, Modal } from "semantic-ui-react";
import { i18next } from "@translations/oarepo_dashboard";
import { getReceiver, RequestTypeLabel } from "../utils";
import RequestStatusLabel from "@js/invenio_requests/request/RequestStatusLabel";
import { DateTime } from "luxon";
import { usePostRequestAction } from "../../hooks";
import _get from "lodash/get";
import { Loader } from "semantic-ui-react";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient } from "@tanstack/react-query";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 0,
    },
  },
});

const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

const RequestsResultsItemTemplateDashboard = ({ result }) => {
  const [open, setOpen] = useState(false);

  if (!result) {
    return <Loader />;
  }

  const detailPageUrl = result?.links?.self_html;
  let creatorName = result.created_by.label;
  const getUserIcon = (receiver) => {
    return receiver?.is_ghost ? "user secret" : "users";
  };
  const cancelLink = _get(result, "links.actions.cancel");

  const { mutate: cancelRequest, isLoading: isCanceling } =
    usePostRequestAction(cancelLink);

  const cancelRequestAction = async () => {
    await cancelRequest(cancelLink, {
      onSuccess: () => {
        location.reload();
      },
    });
  };

  const handleCancelClick = (e) => {
    e.preventDefault();
    setOpen(true);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setOpen(false);
    await cancelRequestAction();
  };

  const handleClose = () => setOpen(false);

  const title = result?.title || "Recreate the request for the title";

  return (
    <Card
      key={result.id}
      href={detailPageUrl}
      fluid
      className="search-listing-item"
    >
      <Card.Content>
        <Item.Extra>
          {result.type && (
            <RequestTypeLabel requestName={result.name || result.type} />
          )}
          {result.status && <RequestStatusLabel status={result.status_code} />}
        </Item.Extra>
        <Card.Header style={{ marginTop: "1.2rem", fontSize: "1.5rem" }}>
          {title}
        </Card.Header>
        <Card.Meta style={{ marginTop: "1rem" }}>
          <List>
            <List.Item>
              <List.Icon color="orange" name="user outline" />
              <List.Content>
                <small>
                  {i18next.t("Opened by {{creatorName}} on {{created}}.", {
                    creatorName: creatorName,
                    created: result.created,
                    interpolation: { escapeValue: false },
                  })}{" "}
                  {result.receiver && getReceiver(result)}
                </small>
              </List.Content>
            </List.Item>
            {result.expires_at && (
              <List.Item>
                <List.Icon color="orange" name="calendar outline" />
                <List.Content>
                  <small>
                    {i18next.t("Expires at: {{- expiringDate}}", {
                      expiringDate: DateTime.fromISO(
                        result.expires_at,
                      ).toLocaleString(i18next.language),
                    })}
                  </small>
                </List.Content>
              </List.Item>
            )}
          </List>
        </Card.Meta>
        <Card.Description style={{ marginTop: "2rem" }}>
          {result?.description || i18next.t("No description")}
        </Card.Description>
      </Card.Content>
      <Card.Content extra>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            {result.receiver?.community &&
              result.expanded?.receiver.metadata.title && (
                <>
                  <Icon
                    className="default-margin"
                    name={getUserIcon(result.expanded?.receiver)}
                  />
                  <span className="ml-5">
                    {result.expanded?.receiver.metadata.title}
                  </span>
                </>
              )}
          </div>
          {cancelLink && (
            <div>
              <Button
                size="small"
                onClick={handleCancelClick}
                loading={isCanceling}
                disabled={isCanceling}
                negative
              >
                Cancel Request
              </Button>
              <Modal open={open} onClose={handleClose} size="small">
                <Modal.Header>Confirm cancel</Modal.Header>
                <Modal.Content>
                  <p>Are you sure you want to cancel this request?</p>
                </Modal.Content>
                <Modal.Actions>
                  <Button onClick={handleClose}>No</Button>
                  <Button
                    negative
                    onClick={handleConfirm}
                    loading={isCanceling}
                  >
                    Yes, Cancel
                  </Button>
                </Modal.Actions>
              </Modal>
            </div>
          )}
        </div>
      </Card.Content>
    </Card>
  );
};

const RequestResultsItemWithQueryProvider = ({ result }) => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
    >
      <RequestsResultsItemTemplateDashboard result={result} />
    </PersistQueryClientProvider>
  );
};

export default RequestResultsItemWithQueryProvider;
